import React, { useRef, useState } from 'react';
import { api, tokens } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day, qty } from '../lib/format.js';
import { ROLES, can } from '../lib/roles.js';
import { Alert, Card, CardSkeleton, Empty, Field, useAsync } from '../components/ui.jsx';

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * The PDF is the request body, so this cannot go through api.post (JSON).
 * Returns { status, body } rather than throwing: a 409 or 422 is a question
 * for the person, not a failure.
 */
async function uploadPdf({ entityId, file, batchNo, replace }) {
  const q = new URLSearchParams({ entity_id: entityId, filename: file.name });
  if (batchNo) q.set('batch_no', batchNo);
  if (replace) q.set('replace', 'true');
  const send = () => fetch(`/api/test-certificates?${q}`, {
    method: 'POST',
    headers: { 'content-type': 'application/pdf', authorization: `Bearer ${(tokens.get() || {}).access_token || ''}` },
    body: file,
  });
  let res = await send();
  if (res.status === 401) { await api.get('/api/auth/me').catch(() => {}); res = await send(); }
  const body = await res.json().catch(() => ({}));
  return { status: res.status, body };
}

/**
 * Test Certificates arrive from the mill or the supplier as one PDF per batch.
 * Each is filed under its batch number and then goes out with every invoice
 * that sells from that batch — by email, on WhatsApp, and in the print set.
 */
export default function TestCertificates() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const canFile = can(session, ROLES.RECORD_SPEC);
  const canDelete = can(session, ROLES.ADMIN_ACCOUNTS);
  const [search, setSearch] = useState('');
  const filed = useAsync(() => api.get(`/api/test-certificates?entity_id=${entityId}${search ? `&search=${encodeURIComponent(search)}` : ''}`), [entityId, search]);
  const missing = useAsync(() => api.get(`/api/test-certificates/missing?entity_id=${entityId}`), [entityId]);
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);
  const forBatch = useRef(null);

  const reload = () => { filed.reload(); missing.reload(); };
  const patch = (key, p) => setQueue((qs) => qs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const run = async (item, { batchNo, replace } = {}) => {
    patch(item.key, { state: 'uploading', message: null });
    try {
      const { status, body } = await uploadPdf({ entityId, file: item.file, batchNo: batchNo || item.batchNo, replace });
      if (status === 200 || status === 201) {
        const c = body.certificate;
        patch(item.key, {
          state: 'done', batchNo: c.batch_no,
          message: body.unchanged ? 'Already filed — same file' : body.replaced ? 'Replaced the earlier certificate' : 'Filed',
        });
        reload();
      } else if (status === 422 && body.error === 'batch_not_found') {
        patch(item.key, { state: 'ask', message: body.message, candidates: body.candidates || [] });
      } else if (status === 409) {
        patch(item.key, { state: 'exists', message: body.message, batchNo: batchNo || item.batchNo });
      } else {
        patch(item.key, { state: 'error', message: body.message || `Upload failed (${status})` });
      }
    } catch (e) { patch(item.key, { state: 'error', message: e.message }); }
  };

  const add = (files, batchNo) => {
    setError(null);
    const pdfs = [...files].filter((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    if (pdfs.length < files.length) setError('Only PDF files can be filed; the others were left out.');
    const items = pdfs.map((file, n) => ({ key: `${Date.now()}-${n}-${file.name}`, file, batchNo: batchNo || '', state: 'waiting' }));
    setQueue((qs) => [...items, ...qs]);
    // One at a time: a mill's batch of fifty PDFs should not open fifty uploads.
    items.reduce((p, it) => p.then(() => run(it)), Promise.resolve());
  };

  const pickFor = (batchNo) => { forBatch.current = batchNo; input.current.click(); };

  const remove = async (c) => {
    if (!window.confirm(`Remove the certificate for batch ${c.batch_no}? Invoices sent from now on will go without it.`)) return;
    try { await api.delete(`/api/test-certificates/${c.id}`); reload(); } catch (e) { setError(e.message); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Test certificates</h1>
          <div className="page-sub">
            The supplier's certificate for each batch, filed under its batch number. Every invoice that sells from a
            batch carries its certificate — once per batch, however the batch is split.
          </div>
        </div>
      </div>

      <Alert>{error}</Alert>

      {canFile && (
        <Card title="Upload certificates"
          note="Name each PDF after its batch number (T42504.pdf, or TC T42504 SAIL.pdf) and it is filed by itself. A name that does not settle it asks you.">
          <input ref={input} type="file" accept="application/pdf,.pdf" multiple hidden
            onChange={(e) => { add(e.target.files, forBatch.current); forBatch.current = null; e.target.value = ''; }} />
          <div className={`dropzone${drag ? ' over' : ''}`} role="button" tabIndex={0}
            onClick={() => input.current.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}>
            <b>Drop TC PDFs here</b>, or click to choose. Several at once is fine.
          </div>
          {queue.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table>
                <thead><tr><th>File</th><th>Batch number</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {queue.map((it) => <UploadRow key={it.key} item={it} onRun={(o) => run(it, o)} />)}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card title="In stock without a certificate"
        note="Batch numbers with stock and nothing filed. Invoices from these go out without a TC.">
        {missing.loading && !missing.data && <CardSkeleton lines={3} />}
        <Alert onRetry={missing.reload}>{missing.error}</Alert>
        {missing.data && (missing.data.batches.length === 0
          ? <Empty title="Every batch in stock has its certificate" />
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Batch</th><th>Item</th><th className="r">Available</th><th /></tr></thead>
                <tbody>
                  {missing.data.batches.map((b) => (
                    <tr key={b.batch_no}>
                      <td className="mono">{b.batch_no}</td>
                      <td>{b.items.join(', ')}</td>
                      <td className="r num">{qty(b.qty_available, b.uom)}</td>
                      <td className="r">{canFile && <button className="btn small secondary" onClick={() => pickFor(b.batch_no)}>Upload TC</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>

      <Card title="Filed certificates"
        actions={(
          <Field label="Search">
            <input type="search" placeholder="Batch or file name" defaultValue={search}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(e.currentTarget.value.trim())}
              onBlur={(e) => setSearch(e.target.value.trim())} />
          </Field>
        )}>
        {filed.loading && !filed.data && <CardSkeleton lines={3} />}
        <Alert onRetry={filed.reload}>{filed.error}</Alert>
        {filed.data && (filed.data.certificates.length === 0
          ? <Empty title={search ? 'No certificate matches' : 'No certificates filed yet'} />
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Batch</th><th>Item</th><th className="r">In stock</th><th>File</th><th>Filed</th><th /></tr></thead>
                <tbody>
                  {filed.data.certificates.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">{c.batch_no}</td>
                      <td>{c.items.length ? c.items.join(', ') : <span className="muted">no batch in InvIT yet</span>}</td>
                      <td className="r num">{Number(c.qty_available) > 0 ? qty(c.qty_available) : '—'}</td>
                      <td>{c.filename}<div className="sub">{kb(c.size_bytes)}</div></td>
                      <td>{day(c.updated_at)}<div className="sub">{c.uploaded_by_name || ''}</div></td>
                      <td className="r" style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn small ghost" onClick={() => api.open(`/api/test-certificates/${c.id}/file`).catch((e) => setError(e.message))}>View</button>
                        {canFile && <button className="btn small ghost" onClick={() => pickFor(c.batch_no)}>Replace</button>}
                        {canDelete && <button className="btn small ghost" onClick={() => remove(c)}>Remove</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>
    </>
  );
}

function UploadRow({ item, onRun }) {
  const [typed, setTyped] = useState(item.batchNo || '');
  const tone = { done: 'var(--good-text)', error: 'var(--critical-text)', ask: 'var(--warning-text)', exists: 'var(--warning-text)' }[item.state];
  return (
    <tr>
      <td>{item.file.name}<div className="sub">{kb(item.file.size)}</div></td>
      <td>
        {item.state === 'ask' ? (
          <div className="inline" style={{ gap: 6 }}>
            {item.candidates && item.candidates.length > 0 ? (
              <select value={typed} onChange={(e) => setTyped(e.target.value)}>
                <option value="">Choose…</option>
                {item.candidates.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input value={typed} placeholder="Batch number" onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && typed.trim() && onRun({ batchNo: typed.trim() })} />
            )}
            <button className="btn small" disabled={!typed.trim()} onClick={() => onRun({ batchNo: typed.trim() })}>File</button>
          </div>
        ) : <span className="mono">{item.batchNo || '—'}</span>}
      </td>
      <td style={{ color: tone }}>
        {{ waiting: 'Waiting…', uploading: 'Uploading…' }[item.state] || item.message}
      </td>
      <td className="r">
        {item.state === 'exists' && <button className="btn small secondary" onClick={() => onRun({ batchNo: item.batchNo, replace: true })}>Replace</button>}
        {item.state === 'error' && <button className="btn small ghost" onClick={() => onRun({})}>Retry</button>}
      </td>
    </tr>
  );
}
